import React, { useState, useEffect, useRef } from 'react';
import { Send, X, MessageSquare, Calculator, FileText, Search, TrendingUp, ChevronRight, UserCheck, BarChart3, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Ever AI v3.0 - Agente de Ventas Inmobiliario Digital
 * Propósito: Perfilado de leads, asesoría estratégica y motor de recomendación híbrido.
 */

const EverAIAssistant = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);

    // Capa A: Estado del Inventario
    const [inventoryStatus] = useState({
        available: false,
        reason: "Inventario en carga",
        expected: "Próximamente"
    });

    // Capa B: Perfilado del Cliente
    const [userProfile, setUserProfile] = useState({
        budget: null,
        operation: null, // venta, alquiler, inversion
        zone_preference: [],
        property_type: [],
        urgency: null, // alta, media, baja
        family_size: null,
        investment_goal: null,
        step: 0 // Seguimiento del flujo de perfilado
    });

    const messagesEndRef = useRef(null);

    // Knowledge Base Operativo
    const knowledgeBase = {
        comparativa: {
            keywords: ['que es mejor', 'casa o apartamento', 'conviene casa', 'conviene apartamento', 'departamento'],
            response: `🏠 **CASA vs 🏢 APARTAMENTO**\n\n**🏠 Casa:**\n• Pros: Más espacio, privacidad y jardín.\n• Contras: Mayor mantenimiento y menor seguridad integrada.\n\n**🏢 Apartamento:**\n• Pros: Seguridad 24/7, amenidades y menor mantenimiento.\n• Contras: Cuota de condominio y menos privacidad.\n\n💡 **Recomendación:**\n• **Familia:** Casa.\n• **Soltero/Inversión corta:** Apartamento.\n• **Inversión larga (Plusvalía):** Casa.`
        },
        extranjeros: {
            keywords: ['extranjero', 'usa', 'pasaporte', 'poder notarial', 'nit'],
            response: `🌍 **Extranjeros en El Salvador:**\n\n✅ **Sí pueden comprar.**\n\n• **Requisitos:** Pasaporte vigente, NIT y Poder Notarial si compras desde afuera.\n• **Hipotecas:** Acceso limitado según banco; requiere respaldo financiero sólido.\n• **Impuestos:** Los mismos que un local (3% Alcabala).`
        },
        plataforma: {
            keywords: ['como publicar', 'subir', 'editar', 'dashboard', 'leads'],
            response: `🖥️ **Uso de la Plataforma:**\n\n1. Entra al **Dashboard**.\n2. Clic en **"+ Publicar Inmueble"**.\n3. Sube 8-12 fotos claras.\n4. **Tip:** Anuncios con buenas fotos contactan 3x más.`
        },
        cnr: {
            keywords: ['cnr', 'extracto', 'gravamen', 'hipoteca', 'embargo'],
            response: `📄 **Procesos en el CNR:**\n\n• **Libertad de Gravamen:** Costo ~$11.43, tiempo 1-3 días. Confirma que no hay embargos.\n• **Certificación de Extracto:** Confirma la información legal total de la propiedad.`
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setTimeout(() => {
                const welcomeMsg = {
                    id: 1,
                    text: "Hola, soy **Ever AI**, tu agente inmobiliario digital. \n\nNo solo busco propiedades, te ayudo a tomar la mejor decisión de inversión en El Salvador. \n\n¿Buscas comprar, vender o invertir hoy?",
                    sender: 'ai',
                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };
                setMessages([welcomeMsg]);
            }, 500);
        }
    }, [isOpen]);

    useEffect(scrollToBottom, [messages, isTyping]);

    // Lógica de Perfilado Progresivo
    const handleProfileSequence = (text) => {
        const msg = text.toLowerCase();
        let nextResponse = "";
        let newStep = userProfile.step;

        switch (userProfile.step) {
            case 1: // Preguntando por Zona
                setUserProfile(prev => ({ ...prev, zone_preference: [text], step: 2 }));
                nextResponse = "Entendido. 📍 ¿Cuántas habitaciones necesitas? (2-3, 4 o más?)";
                break;
            case 2: // Preguntando por Habitaciones
                setUserProfile(prev => ({ ...prev, family_size: text, step: 3 }));
                nextResponse = "Perfecto. 🎯 ¿Es para vivir o buscas una oportunidad de inversión?";
                break;
            case 3: // Preguntando por Objetivo
                setUserProfile(prev => ({ ...prev, investment_goal: text, step: 0 }));
                nextResponse = "Excelente. He guardado tu perfil. Actualmente estamos actualizando el inventario con nuevas propiedades que encajan contigo.\n\nTe avisaré apenas entren opciones que cumplan con tus criterios. ¿Hay algo más en lo que pueda asesorarte mientras tanto?";
                break;
            default:
                break;
        }

        return nextResponse;
    };

    const handleSend = (text) => {
        const messageText = text || inputValue;
        if (!messageText.trim()) return;

        const newUserMsg = {
            id: Date.now(),
            text: messageText,
            sender: 'user',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, newUserMsg]);
        setInputValue('');
        setIsTyping(true);

        setTimeout(() => {
            let aiResponseText = "";
            const searchMsg = messageText.toLowerCase();

            // Detectar flujo de perfilado activo
            if (userProfile.step > 0) {
                aiResponseText = handleProfileSequence(messageText);
            }

            // Si no hay respuesta de perfilado o no hay perfilado activo, buscar en KB
            if (!aiResponseText) {
                // Iniciar perfilado si busca comprar/invertir/precios
                if ((searchMsg.includes('busco') || searchMsg.includes('comprar') || searchMsg.includes('casa de') || searchMsg.includes('precio')) && !inventoryStatus.available) {
                    aiResponseText = `¡Excelente elección! 🏠\nConozco muy bien el mercado. Actualmente el inventario se está actualizando para ofrecerte lo mejor.\n\nPara prepararte las recomendaciones exactas apenas estén disponibles, dime:\n\n1️⃣ **¿En qué zona te interesa?** (Santa Ana, San Salvador o La Libertad)`;
                    setUserProfile(prev => ({ ...prev, step: 1 }));
                }
                // Iniciar perfilado para Inversión
                else if (searchMsg.includes('invertir') || searchMsg.includes('inversion')) {
                    aiResponseText = "Excelente decisión 💰\nPara recomendarte con propiedad, necesito conocer tu perfil:\n\n1️⃣ **¿Cuál es tu presupuesto aproximado?**";
                    setUserProfile(prev => ({ ...prev, step: 1 }));
                }
                // Respuestas de Knowledge Base
                else {
                    for (const key in knowledgeBase) {
                        if (knowledgeBase[key].keywords.some(kw => searchMsg.includes(kw))) {
                            aiResponseText = knowledgeBase[key].response;
                            break;
                        }
                    }

                    if (!aiResponseText) {
                        // Fallback obligatorio según lo solicitado
                        aiResponseText = "Buena pregunta. Como tu agente digital, puedo asesorarte en **Estrategia de Inversión**, **Comparativas** o **CNR**. Pero si necesitas algo muy específico, un **agente humano** puede ayudarte con más detalle. ¿Qué área prefieres explorar?";
                    }
                }
            }

            const aiMsg = {
                id: Date.now() + 1,
                text: aiResponseText,
                sender: 'ai',
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };

            setMessages(prev => [...prev, aiMsg]);
            setIsTyping(false);
        }, 1200);
    };

    const QuickAction = ({ icon: Icon, label, onClick }) => (
        <button
            onClick={onClick}
            className="flex items-center gap-2 px-3 py-2 bg-white/50 hover:bg-white border border-gray-100 rounded-full text-xs font-medium text-gray-700 transition-all shadow-sm"
        >
            <Icon size={14} className="text-[#667eea]" />
            {label}
        </button>
    );

    return (
        <div className="ever-ai-container" style={{ position: 'fixed', bottom: '24px', left: '24px', zIndex: 9999 }}>
            {/* Panel de Chat */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 50 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 50 }}
                        className="mb-4 w-[360px] max-w-[calc(100vw-48px)] h-[550px] bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-4 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white flex items-center justify-between shadow-lg">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-white border-2 border-white/30 flex items-center justify-center text-[#764ba2] font-bold shadow-md">EQ</div>
                                <div>
                                    <h3 className="font-bold text-sm">Ever AI</h3>
                                    <div className="flex items-center text-[10px] text-white/80">
                                        <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div> 🟢 Agente en línea
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => setIsOpen(false)} className="hover:bg-white/10 p-1.5 rounded-full transition-colors"><X size={20} /></button>
                        </div>

                        {/* Mensajes */}
                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-[#f9fafb]">
                            {messages.map((msg) => (
                                <div key={msg.id} className={`mb-4 flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 shadow-sm text-sm ${msg.sender === 'user' ? 'bg-[#764ba2] text-white rounded-tr-none' : 'bg-white text-gray-800 border border-gray-100 rounded-tl-none leading-relaxed'}`}>
                                        <div dangerouslySetInnerHTML={{ __html: msg.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />
                                        <span className={`block text-[10px] mt-1 ${msg.sender === 'user' ? 'text-white/70' : 'text-gray-400'}`}>{msg.time}</span>
                                    </div>
                                </div>
                            ))}
                            {isTyping && (
                                <div className="flex justify-start mb-4">
                                    <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
                                        <span className="text-[10px] text-gray-500 italic">Ever AI está analizando tu perfil...</span>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Botones */}
                        {messages.length < 5 && !isTyping && (
                            <div className="px-4 py-3 flex flex-wrap gap-2 bg-[#f9fafb]">
                                <QuickAction icon={Search} label="🏠 Comprar Casa" onClick={() => handleSend("Busco comprar una propiedad")} />
                                <QuickAction icon={TrendingUp} label="💰 Invertir Capital" onClick={() => handleSend("Quiero invertir en bienes raíces")} />
                                <QuickAction icon={UserCheck} label="🌍 Extranjeros" onClick={() => handleSend("¿Cómo puedo comprar siendo extranjero?")} />
                                <QuickAction icon={BarChart3} label="📊 Comparar Modelos" onClick={() => handleSend("¿Qué es mejor casa o apartamento?")} />
                            </div>
                        )}

                        {/* Input */}
                        <div className="p-4 bg-white border-t border-gray-100">
                            <div className="flex items-center gap-2 bg-gray-50 rounded-2xl px-3 py-1.5 border border-gray-100 focus-within:bg-white transition-all shadow-inner">
                                <input
                                    type="text" value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                    placeholder="Habla con tu agente digital..."
                                    className="flex-1 bg-transparent border-none outline-none text-sm py-2 text-gray-700"
                                    disabled={isTyping}
                                />
                                <button
                                    onClick={() => handleSend()} disabled={!inputValue.trim() || isTyping}
                                    className={`p-2 rounded-xl transition-all ${inputValue.trim() && !isTyping ? 'bg-[#764ba2] text-white shadow-md' : 'bg-gray-200 text-gray-400'}`}
                                >
                                    <Send size={18} />
                                </button>
                            </div>
                            <p className="text-[10px] text-center text-gray-400 mt-2">Ever AI v3.5 • Digital Sales Agent • Servicios Futura</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Botón Flotante Ever AI - ÍCONO ESTILO CHATGPT */}
            <button
                className="ai-assistant-button"
                id="openAIChat"
                aria-label="Abrir asistente Ever AI"
                title="Asistente virtual"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="gpt-icon-container">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="gpt-svg">
                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" opacity="0.3" />
                        <motion.circle
                            innerRef={null}
                            cx="12" cy="12" r="6"
                            stroke="white"
                            strokeWidth="1.5"
                            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        />
                        <motion.circle
                            innerRef={null}
                            cx="12" cy="12" r="3"
                            fill="white"
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        />
                        <path d="M12 8V16M8 12H16" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
                    </svg>
                </div>
            </button>

            <style>
                {`
                .ai-assistant-button {
                    position: fixed;
                    bottom: 24px;
                    left: 24px;
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #667eea, #764ba2);
                    border: 2px solid rgba(255,255,255,.2);
                    box-shadow: 0 8px 32px rgba(102,126,234,.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all .4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    z-index: 40;
                }
                
                /* Tamaño reducido en móvil */
                @media (max-width: 640px) {
                    .ai-assistant-button {
                        width: 48px;
                        height: 48px;
                        bottom: 16px;
                        left: 16px;
                    }
                }

                .gpt-icon-container {
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                @media (max-width: 640px) {
                    .gpt-icon-container {
                        width: 24px;
                        height: 24px;
                    }
                }

                .gpt-svg {
                    width: 100%;
                    height: 100%;
                }

                .ai-assistant-button:hover {
                    transform: translateY(-5px) rotate(5deg);
                    box-shadow: 0 15px 45px rgba(102,126,234,.7);
                }

                .ai-assistant-button:active {
                    transform: scale(0.95);
                }

                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #e5e7eb;
                    border-radius: 10px;
                }
                `}
            </style>
        </div>
    );
};

export default EverAIAssistant;
